import { useZodForm } from 'shared/forms';

import { useScheduleSchema } from './useScheduleSchema';
import type { ScheduleFormValues } from './useScheduleSchema';
import { DEFAULT_TIME_SLOT, DEFAULT_WEEKLY_RUN_ON, SCHEDULE_FORM_FIELDS, SCHEDULE_FREQUENCY } from '../consts';
import { getScheduleForFrequency, isScheduleFrequency } from '../utils';

interface Props {
  /** Handles a valid submission. */
  onSubmit: (values: ScheduleFormValues) => void | Promise<void>;
  /** Schedule saved on the report, read on mount; `null` when the report has none yet, which starts a weekly schedule. */
  savedSchedule: ScheduleFormValues | null;
}

const FIRST_SCHEDULE: ScheduleFormValues = {
  scheduleFrequency: SCHEDULE_FREQUENCY.WEEKLY,
  scheduleRunOn: DEFAULT_WEEKLY_RUN_ON,
  scheduleTimeSlot: DEFAULT_TIME_SLOT
};

export const useScheduleForm = ({ onSubmit, savedSchedule }: Props) => {
  const schema = useScheduleSchema();
  const { form, handleFormSubmit } = useZodForm({
    schema,
    defaultValues: savedSchedule ?? FIRST_SCHEDULE,
    onSubmit
  });
  const { getValues, setValue } = form;

  const handleFrequencyChange = (value: string) => {
    if (!isScheduleFrequency(value)) {
      return;
    }

    const { scheduleFrequency, scheduleRunOn, scheduleTimeSlot } = getScheduleForFrequency({
      frequency: value,
      timeSlot: getValues(SCHEDULE_FORM_FIELDS.TIME_SLOT)
    });
    const setValueOptions = { shouldDirty: true, shouldValidate: true };

    setValue(SCHEDULE_FORM_FIELDS.FREQUENCY, scheduleFrequency, setValueOptions);
    setValue(SCHEDULE_FORM_FIELDS.RUN_ON, scheduleRunOn, setValueOptions);
    setValue(SCHEDULE_FORM_FIELDS.TIME_SLOT, scheduleTimeSlot, setValueOptions);
  };

  return { form, handleFormSubmit, handleFrequencyChange };
};
